import { z } from '@customerio/hydra-validation';
import type { Newsletter as ApiNewsletter } from '@customerio/hydra-ui-api';
import { defineEntity } from '../shared/define-entity';

/**
 * Newsletter (broadcast) entity — workspace-scoped.
 *
 * Minimal detail-only entity. The wire already encodes `id` as a string, so
 * no normalization is needed; the read schema is a passthrough today. Date
 * transforms / a list+mutation surface can be added when a consumer needs
 * them.
 */
export type Newsletter = ApiNewsletter;

const NewsletterFromApi = z.looseObject({}).pipe(z.custom<Newsletter>());

/**
 * The status filter on the Broadcasts newsletters list. `all` is a real
 * sentinel the backend understands, not an "omit the param" marker: its list
 * handler leaves the status filter unapplied for both `""` and `"all"`, and
 * its sort explicitly pairs the two when picking the sent-descending
 * ordering. So the selected value goes on the wire as-is.
 */
export type NewsletterStatusFilter = 'all' | 'drafted' | 'scheduled' | 'sent';

/** The channel filter's "no filter" sentinel — also understood server-side. */
export const ALL_NEWSLETTER_CHANNELS = 'all';

/** The status filter's default. */
export const ALL_NEWSLETTER_STATUSES = 'all';

/**
 * Filters on the Broadcasts newsletters list.
 *
 * `page` is 1-based here and converted at the request boundary, matching the
 * campaign list params. Id arrays are strings because every id in this layer
 * is a string; they are converted to the numbers the wire wants when the
 * query is built.
 */
export interface NewsletterListParams {
  page: number;
  /** A channel name, or `all` for no channel filter. */
  channel?: string;
  type?: NewsletterStatusFilter;
  /** Server-side, case-insensitive substring match on newsletter names. */
  search?: string;
  sort?: string;
  asc?: boolean;
  /** Inclusive lower bound, unix seconds, on the date the sort key selects. */
  startTime?: number;
  /** Inclusive upper bound, unix seconds. */
  endTime?: number;
  sendToUnsubscribed?: boolean;
  subscriptionTopicId?: number;
  tagIds?: readonly string[];
  objectTypeIds?: readonly string[];
  goalIds?: readonly string[];
}

/**
 * `undefined` is part of the params union so the unfiltered workspace-wide
 * read (`getNewslettersQueryOptions`) keeps its own cache slot alongside the
 * filtered Broadcasts list. Both sit under `newsletterKeys.lists`, so one
 * prefix invalidation after a mutation covers them together.
 */
/** @public */
export const newsletter = defineEntity<
  Newsletter,
  Newsletter,
  NewsletterListParams | undefined,
  undefined,
  'workspace'
>({
  name: 'newsletter',
  scope: 'workspace',
  pk: (n) => n.id,
  schema: { read: NewsletterFromApi },
});

export const newsletterKeys = newsletter.keys;

/** Optional filters on the newsletter catalog reads. */
export interface NewsletterCatalogScope {
  /** Case-insensitive substring match on newsletter names. */
  search?: string;
}

/**
 * One catalog slot (the server-paged, optionally searched picker list). Kept
 * under the entity's `all` prefix so entity-wide invalidation covers it.
 */
export const newsletterCatalogKey = (workspaceId: number, scope: NewsletterCatalogScope) =>
  [...newsletterCatalogPrefix(workspaceId), scope] as const;

/**
 * Every catalog slot for the workspace, whatever the scope. Lets a mutation
 * invalidate the catalogs without reaching the entity's `all` prefix, which
 * would refetch every cached newsletter detail too.
 */
export const newsletterCatalogPrefix = (workspaceId: number) =>
  [...newsletterKeys.all(workspaceId), 'catalog'] as const;

/**
 * A newsletter's schedule, kept under its detail slot so anything that invalidates the
 * newsletter invalidates its schedule with it.
 */
export const newsletterScheduleKey = (workspaceId: number, newsletterId: string) =>
  [...newsletterKeys.detail(workspaceId, newsletterId), 'schedule'] as const;

/** A newsletter's delivery metrics, kept under its detail slot for the same reason as its schedule. */
export const newsletterMetricKey = (workspaceId: number, newsletterId: string) =>
  [...newsletterKeys.detail(workspaceId, newsletterId), 'metric'] as const;

export function parseNewsletter(raw: ApiNewsletter): Newsletter {
  return newsletter.parse(raw);
}
